import { Chess } from "chess.js";

// Undo move logic for single-player bot game
export function undoMove(fenHistory) {
  if (!fenHistory || fenHistory.length < 2) return { chess: null, newHistory: fenHistory };
  let newHistory = [...fenHistory];
  newHistory.pop(); // Undo bot move
  if (newHistory.length > 1) newHistory.pop(); // Undo user move
  const lastFen = newHistory[newHistory.length - 1];
  const chess = new Chess(lastFen);
  return { chess, newHistory };
}

// Make a user move if valid
export function makeUserMove(game, from, to, promotion = undefined) {
  if (!game) return { move: null, newGame: game };
  const moves = game.moves({ square: from, verbose: true });
  let validMove;
  if (promotion) {
    validMove = moves.find(move => move.to === to && move.promotion === promotion);
  }
  if (!validMove) {
    // fallback: find any valid move to the destination (for non-promotion moves)
    validMove = moves.find(move => move.to === to);
  }
  if (!validMove) return { move: null, newGame: game };
  const move = game.move(validMove);
  return { move, newGame: new Chess(game.fen()) };
}

// Make a bot move based on difficulty
export function makeBotMove(game, difficulty) {
  if (!game || game.isGameOver()) return { move: null, newGame: game };
  const moves = game.moves({ verbose: true });
  if (moves.length === 0) return { move: null, newGame: game };
  let move;
  if (difficulty === "easy") {
    move = moves[Math.floor(Math.random() * moves.length)];
  } else if (difficulty === "medium") {
    const captureMoves = moves.filter(m => m.captured);
    if (captureMoves.length > 0) {
      const pieceValues = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 100 };
      captureMoves.sort((a, b) => (pieceValues[b.captured] || 0) - (pieceValues[a.captured] || 0));
      move = captureMoves[0];
    } else {
      move = moves[Math.floor(Math.random() * moves.length)];
    }
  } else if (difficulty === "hard") {
    move = findBestMove(game, 2);
    if (!move) move = moves[Math.floor(Math.random() * moves.length)];
  }
  game.move(move);
  return { move, newGame: new Chess(game.fen()) };
}

// Minimax and helpers
export function findBestMove(chess, depth) {
  let bestMove = null;
  let bestValue = -Infinity;
  const moves = chess.moves({ verbose: true });
  for (const move of moves) {
    const temp = new Chess(chess.fen());
    temp.move(move);
    const value = minimax(temp, depth - 1, false);
    if (value > bestValue) {
      bestValue = value;
      bestMove = move;
    }
  }
  return bestMove;
}

export function minimax(chess, depth, isMaximizing) {
  if (depth === 0 || chess.isGameOver()) {
    return evaluateBoard(chess);
  }
  const moves = chess.moves({ verbose: true });
  if (isMaximizing) {
    let maxEval = -Infinity;
    for (const move of moves) {
      const temp = new Chess(chess.fen());
      temp.move(move);
      const evalScore = minimax(temp, depth - 1, false);
      if (evalScore > maxEval) maxEval = evalScore;
    }
    return maxEval;
  } else {
    let minEval = Infinity;
    for (const move of moves) {
      const temp = new Chess(chess.fen());
      temp.move(move);
      const evalScore = minimax(temp, depth - 1, true);
      if (evalScore < minEval) minEval = evalScore;
    }
    return minEval;
  }
}

export function evaluateBoard(chess) {
  const pieceValues = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };
  let evalScore = 0;
  const board = chess.board();
  for (let i = 0; i < 8; i++) {
    for (let j = 0; j < 8; j++) {
      const piece = board[i][j];
      if (piece) {
        const value = pieceValues[piece.type] || 0;
        evalScore += piece.color === "b" ? value : -value;
      }
    }
  }
  return evalScore;
}

export function getResult(chess) {
  if (chess.isCheckmate()) {
    return chess.turn() === "w" ? "Bot wins!" : "You win!";
  }
  if (chess.isDraw()) return "Draw!";
  return null;
}
